import React, { useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import { Lightbulb, ListChecks, HelpCircle, Save, CheckCircle, X } from "lucide-react";
import { useConsultation } from "../../context/ConsultationContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import AutoSaveIndicator from "./AutoSaveIndicator";

export default function ClientNoteForm({ booking, onClose, onSaved }) {
  const { saveClientNote, getClientNote } = useConsultation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const [form, setForm] = useState({
    insights: "",
    action_plan: "",
    next_questions: ""
  });

  const buildPayload = useCallback((formData) => ({
    insights: formData.insights || "",
    action_plan: formData.action_plan || "",
    next_questions: formData.next_questions || ""
  }), []);

  const buildInsertPayload = useCallback((formData) => ({
    insights: formData.insights || "",
    action_plan: formData.action_plan || "",
    next_questions: formData.next_questions || "",
    booking_id: booking.id,
    client_id: booking.clientId,
    expert_id: booking.expertId
  }), [booking.id, booking.clientId, booking.expertId]);

  const {
    autoSaving,
    lastAutoSaved,
    restoredFromDraft,
    setRestoredFromDraft,
    scheduleAutoSave,
    cancelAutoSave,
    markSaved,
    loadFromLocal
  } = useAutoSave({
    table: "client_notes",
    bookingId: booking.id,
    form,
    buildPayload,
    buildInsertPayload,
    enabled: !loadingExisting
  });

  useEffect(() => {
    const load = async () => {
      const existing = await getClientNote(booking.id);
      if (existing) {
        setForm({
          insights: existing.insights || "",
          action_plan: existing.action_plan || "",
          next_questions: existing.next_questions || ""
        });
      } else {
        const localDraft = loadFromLocal();
        if (localDraft) {
          setForm(localDraft);
          setRestoredFromDraft(true);
        }
      }
      setLoadingExisting(false);
    };
    load();
  }, [booking.id, getClientNote, loadFromLocal, setRestoredFromDraft]);

  const handleSave = async () => {
    cancelAutoSave();
    setSaving(true);
    const result = await saveClientNote({
      booking_id: booking.id,
      client_id: booking.clientId,
      expert_id: booking.expertId,
      insights: form.insights,
      action_plan: form.action_plan,
      next_questions: form.next_questions
    });
    setSaving(false);
    if (result) {
      markSaved();
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (onSaved) onSaved(result);
      }, 1500);
    }
  };

  const update = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    scheduleAutoSave(newForm);
  };

  if (loadingExisting) {
    return (
      <div id="client-note-form_loading" className="p-6 text-center">
        <p className="text-lg font-extralight text-neutral-400">Загрузка...</p>
      </div>
    );
  }

  const fields = [
    {
      key: "insights",
      label: "Что запомнилось",
      placeholder: "Какие мысли или инсайты появились после консультации?",
      icon: Lightbulb,
      rows: 3
    },
    {
      key: "action_plan",
      label: "Что планирую сделать",
      placeholder: "Какие шаги вы собираетесь предпринять?",
      icon: ListChecks,
      rows: 3
    },
    {
      key: "next_questions",
      label: "Вопросы на следующий раз",
      placeholder: "Что хотите обсудить на следующей консультации?",
      icon: HelpCircle,
      rows: 2
    }
  ];

  return (
    <motion.div
      id="client-note-form_container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-md bg-neutral-900 border border-neutral-800"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-medium text-white">Мои заметки</h3>
          <p className="text-lg font-extralight text-neutral-400 mt-1">
            Только вы видите эти записи
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AutoSaveIndicator
            autoSaving={autoSaving}
            lastAutoSaved={lastAutoSaved}
            restoredFromDraft={restoredFromDraft}
            onDismissRestore={() => setRestoredFromDraft(false)}
          />
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
              <field.icon className="w-4 h-4 text-accent-400" />
              {field.label}
            </label>
            <textarea
              value={form[field.key]}
              onChange={(e) => update(field.key, e.target.value)}
              rows={field.rows}
              placeholder={field.placeholder}
              className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-accent-500 resize-none transition-colors"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-6">
        <div>
          {saved && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 text-accent-400"
            >
              <CheckCircle className="w-5 h-5" />
              <span className="text-lg font-extralight">Заметки сохранены</span>
            </motion.div>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
        >
          <Save className="w-5 h-5" />
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
    </motion.div>
  );
}
